(() => {
  const roots = document.querySelectorAll("[data-headwear-builder-v2]");
  if (!roots.length) return;

  const qs = (root, selector) => root.querySelector(selector);
  const qsa = (root, selector) => [...root.querySelectorAll(selector)];
  const normalize = (value) => String(value || "").trim().toUpperCase();

  const formatMoney = (cents, currency) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD"
    }).format((Number(cents) || 0) / 100);

  roots.forEach((root) => {
    if (root.dataset.ready === "true") return;
    root.dataset.ready = "true";

    const currency = root.dataset.currency || "USD";

    const decorationVariants = JSON.parse(
      qs(root, "[data-decoration-variants]")?.textContent || "[]"
    );
    const finishingVariants = JSON.parse(
      qs(root, "[data-finishing-variants]")?.textContent || "[]"
    );
    const puffNode = qs(root, "[data-puff-variants]");
    const puffVariants = JSON.parse(puffNode?.textContent || "[]");
    const puffSku = normalize(puffNode?.dataset.puffSku || "");
    const skus = JSON.parse(qs(root, "[data-skus]")?.textContent || "{}");

    const decorationMap = new Map(
      decorationVariants
        .filter((variant) => normalize(variant.sku))
        .map((variant) => [normalize(variant.sku), variant])
    );

    const finishingMap = new Map(
      finishingVariants
        .filter((variant) => normalize(variant.sku))
        .map((variant) => [normalize(variant.sku), variant])
    );

    const state = {
      unlocked: 1,
      color: "",
      variantId: 0,
      variantPrice: 0,
      front: {
        placement: "",
        decoration: "",
        file: null,
        thread: ""
      },
      extras: {
        left: { enabled: false, placement: "", decoration: "", file: null, thread: "" },
        right: { enabled: false, placement: "", decoration: "", file: null, thread: "" },
        back: { enabled: false, placement: "", decoration: "", file: null, thread: "" }
      },
      finishing: {
        hangtag: false,
        brim_sticker: false,
        dust_bag: false
      },
      qty: 24,
      discount: 0,
      reviewed: false
    };

    const addButton = qs(root, ".hbv2__add");
    const message = qs(root, "[data-message]");
    const mainImage = qs(root, "[data-main-image]");

    function discountForQty(qty) {
      if (qty >= 288) return 30;
      if (qty >= 144) return 26;
      if (qty >= 72) return 22;
      if (qty >= 48) return 18;
      if (qty >= 36) return 10;
      return 0;
    }

    function tierForQty(qty) {
      if (qty >= 288) return 288;
      if (qty >= 144) return 144;
      if (qty >= 72) return 72;
      if (qty >= 48) return 48;
      if (qty >= 36) return 36;
      return 24;
    }

    function setQuantity(rawValue) {
      const qty = Math.max(24, parseInt(rawValue, 10) || 24);

      state.qty = qty;
      state.discount = discountForQty(qty);

      qsa(root, "[data-tier]").forEach((button) => {
        button.classList.toggle(
          "is-selected",
          Number(button.dataset.qty) === qty
        );
      });

      render();
    }

    function resolvePuffVariant() {
      if (!puffVariants.length) return null;

      if (puffVariants.length === 1 && !puffSku) {
        return puffVariants[0];
      }

      if (!puffSku) return null;

      return (
        puffVariants.find(
          (variant) => normalize(variant.sku) === puffSku
        ) || null
      );
    }

    function addonVariant(group, type) {
      if (type === "3D Puff") {
        return resolvePuffVariant();
      }

      if (group === "finishing") {
        const sku = skus.finishing?.[type];
        return sku ? finishingMap.get(normalize(sku)) || null : null;
      }

      const sku = skus[group]?.[type];
      return sku ? decorationMap.get(normalize(sku)) || null : null;
    }

    function showConnectedPrices() {
      qsa(root, "[data-decoration-price]").forEach((label) => {
        const [group, decoration] =
          String(label.dataset.decorationPrice || "").split("|");

        if (group === "front" && decoration === "Embroidery") {
          label.textContent = "Included";
          label.classList.add("is-included");
          return;
        }

        const variant = addonVariant(group, decoration);

        label.classList.remove("is-included");
        label.textContent = variant
          ? `+${formatMoney(variant.price, currency)}/pc`
          : "Not connected";
      });

      Object.keys(state.finishing).forEach((key) => {
        const variant = addonVariant("finishing", key);
        const label =
          qs(root, `[data-addon-price="${key}"]`) ||
          qs(root, `[data-addon-price-label="${key}"]`);
        const input = qs(root, `[data-finishing="${key}"]`);

        if (!label || !input) return;

        if (!variant) {
          label.textContent = "Not connected";
          input.checked = false;
          input.disabled = true;
          state.finishing[key] = false;
          return;
        }

        label.textContent = `+${formatMoney(variant.price, currency)}/pc`;
        input.disabled = false;
      });
    }

    function selectedAddons() {
      const items = [];

      if (state.front.decoration) {
        const variant = addonVariant("front", state.front.decoration);
        if (variant) {
          items.push({
            label: `${state.front.placement} ${state.front.decoration}`,
            variant
          });
        }
      }

      Object.entries(state.extras).forEach(([key, extra]) => {
        if (!extra.enabled || !extra.decoration || !extra.placement) return;

        const variant = addonVariant(key, extra.decoration);
        if (!variant) return;

        items.push({
          label: `${extra.placement} ${extra.decoration}`,
          variant
        });
      });

      Object.entries(state.finishing).forEach(([key, enabled]) => {
        if (!enabled) return;

        const variant = addonVariant("finishing", key);
        if (!variant) return;

        const finishingNames = {
          hangtag: "Hangtag",
          brim_sticker: "Sticker on brim",
          dust_bag: "Dust bag"
        };

        items.push({
          label: finishingNames[key] || key,
          variant
        });
      });

      return items;
    }

    function render() {
      const setText = (selector, value) => {
        const el = qs(root, selector);
        if (el) el.textContent = value;
      };

      setText("[data-summary-color]", state.color || "—");

      setText(
        "[data-summary-front]",
        state.front.placement && state.front.decoration
          ? `${state.front.placement} · ${state.front.decoration}`
          : "—"
      );

      const extraLabels = Object.values(state.extras)
        .filter(
          (extra) =>
            extra.enabled &&
            extra.placement &&
            extra.decoration
        )
        .map(
          (extra) =>
            `${extra.placement} · ${extra.decoration}`
        );

      setText(
        "[data-summary-extras]",
        extraLabels.length ? extraLabels.join(", ") : "None"
      );

      const finishingNames = {
        hangtag: "Hangtag",
        brim_sticker: "Sticker on brim",
        dust_bag: "Dust bag"
      };

      const selectedFinishing = Object.entries(state.finishing)
        .filter(([, enabled]) => enabled)
        .map(([key]) => finishingNames[key] || key);

      setText(
        "[data-summary-finishing]",
        selectedFinishing.length
          ? selectedFinishing.join(", ")
          : "None"
      );

      const addonUnitCents = selectedAddons().reduce(
        (sum, item) => sum + Number(item.variant.price || 0),
        0
      );

      const discountedBaseCents =
        state.variantPrice * (1 - state.discount / 100);

      const unitTotalCents =
        discountedBaseCents + addonUnitCents;

      const orderTotalCents =
        unitTotalCents * state.qty;

      setText(
        "[data-sum-base]",
        state.color
          ? `${formatMoney(state.variantPrice, currency)}/pc`
          : "—"
      );

      setText(
        "[data-sum-addons]",
        `${formatMoney(addonUnitCents, currency)}/pc + ${formatMoney(0, currency)} once`
      );

      setText("[data-sum-discount]", `${state.discount}%`);

      setText(
        "[data-sum-unit]",
        state.color
          ? formatMoney(Math.round(unitTotalCents), currency)
          : "—"
      );

      setText("[data-sum-qty]", `${state.qty} pcs`);

      setText(
        "[data-sum-total]",
        state.color
          ? formatMoney(Math.round(orderTotalCents), currency)
          : "—"
      );

      if (state.color) {
        setText('[data-step-summary="1"]', state.color);
      }

      if (state.front.placement && state.front.decoration) {
        setText(
          '[data-step-summary="2"]',
          `${state.front.placement} · ${state.front.decoration}`
        );
      }

      setText(
        '[data-step-summary="3"]',
        extraLabels.length
          ? extraLabels.join(" | ")
          : "No additional logos"
      );

      setText(
        '[data-step-summary="4"]',
        selectedFinishing.length
          ? selectedFinishing.join(", ")
          : "No finishing services"
      );

      showConnectedPrices();

      if (addButton) {
        addButton.disabled = !(
          state.reviewed &&
          state.color &&
          state.front.placement &&
          state.front.decoration &&
          state.front.file
        );
      }
    }

    function openStep(stepNumber) {
      if (stepNumber > state.unlocked) return;

      qsa(root, "[data-step]").forEach((step) => {
        step.classList.toggle(
          "is-open",
          Number(step.dataset.step) === stepNumber
        );
      });
    }

    function completeAndOpen(currentStep, nextStep) {
      const current = qs(root, `[data-step="${currentStep}"]`);
      current?.classList.add("is-complete");
      current?.classList.remove("is-open");

      state.unlocked = Math.max(state.unlocked, nextStep);

      openStep(nextStep);

      qs(root, `[data-step="${nextStep}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });

      render();
    }

    function updateFrontNext() {
      const next =
        qs(root, '[data-next="3"]') ||
        qs(root, '[data-next-step="3"]');

      if (!next) return;

      next.disabled = !(
        state.front.placement &&
        state.front.decoration &&
        state.front.file
      );
    }

    qsa(root, "[data-step-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        const stepNumber = Number(
          button.closest("[data-step]")?.dataset.step || 0
        );
        openStep(stepNumber);
      });
    });

    qsa(root, "[data-color-choice]").forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) return;

        qsa(root, "[data-color-choice]").forEach((item) => {
          item.classList.toggle("is-selected", item === button);
        });

        state.color =
          button.dataset.color ||
          button.dataset.colorName ||
          "";

        state.variantId = Number(
          button.dataset.variantId || 0
        );

        state.variantPrice = Number(
          button.dataset.price ||
          button.dataset.variantPrice ||
          0
        );

        const image =
          button.dataset.image ||
          button.dataset.variantImage;

        if (mainImage && image) {
          mainImage.src = image;
          mainImage.srcset = "";
        }

        const colorLabel =
          qs(root, "[data-left-color]") ||
          qs(root, "[data-left-selected-color]");

        if (colorLabel) {
          colorLabel.textContent = state.color;
        }

        const next =
          qs(root, '[data-next="2"]') ||
          qs(root, '[data-next-step="2"]');

        if (next) next.disabled = false;

        render();
      });
    });

    (
      qs(root, '[data-next="2"]') ||
      qs(root, '[data-next-step="2"]')
    )?.addEventListener("click", () => {
      if (state.color) {
        completeAndOpen(1, 2);
      }
    });

    qsa(root, "[data-front-placement]").forEach((button) => {
      button.addEventListener("click", () => {
        qsa(root, "[data-front-placement]").forEach((item) => {
          item.classList.toggle("is-selected", item === button);
        });

        state.front.placement =
          button.dataset.frontPlacement || "";

        const reveal = qs(root, "[data-front-decoration-wrap]");
        if (reveal) reveal.hidden = false;

        render();
      });
    });

    qsa(root, "[data-front-decoration]").forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) return;

        qsa(root, "[data-front-decoration]").forEach((item) => {
          item.classList.toggle("is-selected", item === button);
        });

        state.front.decoration =
          button.dataset.frontDecoration || "";

        const upload = qs(root, "[data-front-upload-wrap]");
        if (upload) upload.hidden = false;

        const thread =
          qs(root, "[data-front-thread]") ||
          qs(root, "[data-front-thread-wrap]");

        if (thread) {
          thread.hidden = state.front.decoration === "DTF";
        }

        if (state.front.decoration === "DTF") {
          state.front.thread = "";
        }

        updateFrontNext();
        render();
      });
    });

    qsa(root, "[data-file], [data-file-input]").forEach((input) => {
      input.addEventListener("change", () => {
        const key =
          input.dataset.file ||
          input.dataset.fileInput;

        const file = input.files?.[0] || null;

        if (key === "front") {
          state.front.file = file;
          updateFrontNext();
        } else if (state.extras[key]) {
          state.extras[key].file = file;
        }

        const fileName = qs(root, `[data-file-name="${key}"]`);
        if (fileName) {
          fileName.textContent = file ? file.name : "";
        }

        render();
      });
    });

    qsa(root, "[data-thread], [data-thread-input]").forEach((input) => {
      input.addEventListener("input", () => {
        const key =
          input.dataset.thread ||
          input.dataset.threadInput;

        if (key === "front") {
          state.front.thread = input.value.trim();
        } else if (state.extras[key]) {
          state.extras[key].thread = input.value.trim();
        }
      });
    });

    (
      qs(root, '[data-next="3"]') ||
      qs(root, '[data-next-step="3"]')
    )?.addEventListener("click", () => {
      if (
        state.front.placement &&
        state.front.decoration &&
        state.front.file
      ) {
        completeAndOpen(2, 3);
      }
    });

    qsa(root, "[data-extra-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        const key = button.dataset.extraToggle;
        const extra = state.extras[key];
        if (!extra) return;

        extra.enabled = !extra.enabled;

        const body = qs(root, `[data-extra-body="${key}"]`);
        if (body) body.hidden = !extra.enabled;

        if (!extra.enabled) {
          extra.placement = "";
          extra.decoration = "";
          extra.file = null;
          extra.thread = "";

          qsa(root, `[data-extra-placement="${key}"]`).forEach(
            (item) => item.classList.remove("is-selected")
          );

          qsa(root, `[data-extra-decoration="${key}"]`).forEach(
            (item) => item.classList.remove("is-selected")
          );
        }

        render();
      });
    });

    qsa(root, "[data-extra-placement]").forEach((button) => {
      button.addEventListener("click", () => {
        const key = button.dataset.extraPlacement;
        const extra = state.extras[key];
        if (!extra) return;

        qsa(root, `[data-extra-placement="${key}"]`).forEach(
          (item) => {
            item.classList.toggle("is-selected", item === button);
          }
        );

        extra.placement =
          button.dataset.placement ||
          button.dataset.placementValue ||
          "";

        const wrap = qs(
          root,
          `[data-extra-decoration-wrap="${key}"]`
        );

        if (wrap) wrap.hidden = false;

        render();
      });
    });

    qsa(root, "[data-extra-decoration]").forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) return;

        const key = button.dataset.extraDecoration;
        const extra = state.extras[key];
        if (!extra) return;

        qsa(root, `[data-extra-decoration="${key}"]`).forEach(
          (item) => {
            item.classList.toggle("is-selected", item === button);
          }
        );

        extra.decoration =
          button.dataset.decoration ||
          button.dataset.decorationValue ||
          "";

        const upload = qs(
          root,
          `[data-extra-upload-wrap="${key}"]`
        );
        if (upload) upload.hidden = false;

        const thread =
          qs(root, `[data-extra-thread="${key}"]`) ||
          qs(root, `[data-extra-thread-wrap="${key}"]`);

        if (thread) {
          thread.hidden = extra.decoration === "DTF";
        }

        if (extra.decoration === "DTF") {
          extra.thread = "";
        }

        render();
      });
    });

    qsa(root, "[data-remove-extra]").forEach((button) => {
      button.addEventListener("click", () => {
        const key = button.dataset.removeExtra;
        const extra = state.extras[key];
        if (!extra) return;

        extra.enabled = false;
        extra.placement = "";
        extra.decoration = "";
        extra.file = null;
        extra.thread = "";

        const body = qs(root, `[data-extra-body="${key}"]`);
        if (body) body.hidden = true;

        render();
      });
    });

    (
      qs(root, '[data-next="4"]') ||
      qs(root, '[data-next-step="4"]')
    )?.addEventListener("click", () => {
      completeAndOpen(3, 4);
    });

    qsa(root, "[data-finishing]").forEach((input) => {
      input.addEventListener("change", () => {
        state.finishing[input.dataset.finishing] =
          input.checked;
        render();
      });
    });

    qsa(root, "[data-tier]").forEach((button) => {
      button.addEventListener("click", () => {
        setQuantity(button.dataset.qty);
      });
    });

    qs(root, "[data-review]")?.addEventListener("click", () => {
      state.reviewed = true;

      const step4 = qs(root, '[data-step="4"]');
      step4?.classList.add("is-complete");
      step4?.classList.remove("is-open");

      render();
    });

    qs(root, "[data-headwear-form]")?.addEventListener(
      "submit",
      async (event) => {
        event.preventDefault();

        if (
          !state.color ||
          !state.front.placement ||
          !state.front.decoration ||
          !state.front.file
        ) {
          if (message) {
            message.hidden = false;
            message.textContent =
              "Complete the required color and front-decoration steps first.";
          }
          return;
        }

        const configurationId =
          `HW-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;

        try {
          const baseForm = new FormData();

          baseForm.set("id", String(state.variantId));
          baseForm.set("quantity", String(state.qty));
          baseForm.set(
            "properties[_Configuration ID]",
            configurationId
          );
          baseForm.set(
            "properties[_Builder Category]",
            "headwear"
          );
          baseForm.set(
            "properties[Hat Color]",
            state.color
          );
          baseForm.set(
            "properties[Front Placement]",
            state.front.placement
          );
          baseForm.set(
            "properties[Front Decoration]",
            state.front.decoration
          );
          baseForm.set(
            "properties[Quantity]",
            String(state.qty)
          );
          baseForm.set(
            "properties[Volume Discount]",
            `${state.discount}%`
          );
          baseForm.set(
            "properties[Front Thread Colors]",
            state.front.decoration === "DTF"
              ? "Not applicable"
              : state.front.thread || "Match uploaded artwork"
          );

          if (state.front.file) {
            baseForm.set(
              "properties[Front Artwork]",
              state.front.file,
              state.front.file.name
            );
          }

          const additionalLogoText = Object.values(state.extras)
            .filter(
              (extra) =>
                extra.enabled &&
                extra.placement &&
                extra.decoration
            )
            .map(
              (extra) =>
                `${extra.placement} ${extra.decoration}`
            )
            .join(" | ");

          if (additionalLogoText) {
            baseForm.set(
              "properties[Additional Logos]",
              additionalLogoText
            );
          }

          Object.entries(state.extras).forEach(([key, extra]) => {
            if (extra.enabled && extra.file) {
              const label =
                `${key.charAt(0).toUpperCase()}${key.slice(1)} Artwork`;

              baseForm.set(
                `properties[${label}]`,
                extra.file,
                extra.file.name
              );
            }
          });

          let response = await fetch(
            `${window.Shopify?.routes?.root || "/"}cart/add.js`,
            {
              method: "POST",
              body: baseForm,
              headers: {
                Accept: "application/json"
              }
            }
          );

          if (!response.ok) {
            throw new Error(await response.text());
          }

          const addOnItems = selectedAddons()
            .filter((item) => item.variant?.available !== false)
            .map((item) => ({
              id: Number(item.variant.id),
              quantity: state.qty,
              properties: {
                "_Configuration ID": configurationId,
                "_Builder Category": "headwear",
                "_Charge Type": "per_piece_addon",
                "_Add On": item.label
              }
            }));

          if (addOnItems.length) {
            response = await fetch(
              `${window.Shopify?.routes?.root || "/"}cart/add.js`,
              {
                method: "POST",
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json"
                },
                body: JSON.stringify({
                  items: addOnItems
                })
              }
            );

            if (!response.ok) {
              throw new Error(await response.text());
            }
          }

          window.location.href =
            `${window.Shopify?.routes?.root || "/"}cart`;
        } catch (error) {
          console.error(error);

          if (message) {
            message.hidden = false;
            message.textContent =
              "Could not add this build. Check your connected add-on products and SKUs.";
          }
        }
      }
    );

    showConnectedPrices();
    setQuantity(24);
    render();
  });
})();